/**
 * Contact Section with split layout: info + form
 */
import { useState } from 'react';
import { motion } from 'motion/react';
import { Mail, Phone, MapPin, Linkedin, Twitter, Github, Youtube, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { content } from '@/landing/data/content';
import { submitContactForm } from '@/api/contact';

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await submitContactForm(formData);
      setIsSuccess(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[var(--navy)] border-t border-[rgba(201,168,76,0.15)] overflow-hidden">
      <div className="absolute inset-0 hero-grid-overlay opacity-40" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[var(--gold)] mb-4">
            Get In Touch
          </h2>
          <p className="text-xl text-[var(--text-muted)] max-w-3xl mx-auto font-light">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Left: Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h3 className="font-display text-3xl font-semibold text-white mb-6">
                Contact Information
              </h3>
              <p className="text-[var(--text-muted)] leading-relaxed mb-8 font-light">
                Reach out to us through any of these channels, and our team will get back to you promptly.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-start gap-4 group"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[rgba(26,86,219,0.3)] border border-[rgba(201,168,76,0.2)] flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[var(--gold)]" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Email</h4>
                  <a href={`mailto:${content.contact.email}`} className="text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors">
                    {content.contact.email}
                  </a>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-start gap-4 group"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[var(--gold-pale)] border border-[rgba(201,168,76,0.2)] flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[var(--gold)]" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Phone</h4>
                  <a href={`tel:${content.contact.phone}`} className="text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors">
                    {content.contact.phone}
                  </a>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-start gap-4 group"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[rgba(26,86,219,0.3)] border border-[rgba(201,168,76,0.2)] flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[var(--royal)]" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Location</h4>
                  <p className="text-[var(--text-muted)]">{content.contact.address}</p>
                </div>
              </motion.div>
            </div>

            {/* Social Media */}
            <div>
              <h4 className="font-semibold text-white mb-4">Follow Us</h4>
              <div className="flex gap-3">
                {[
                  { icon: Linkedin, href: content.contact.socials.linkedin, label: 'LinkedIn' },
                  { icon: Twitter, href: content.contact.socials.twitter, label: 'Twitter' },
                  { icon: Github, href: content.contact.socials.github, label: 'GitHub' },
                  { icon: Youtube, href: content.contact.socials.youtube, label: 'YouTube' },
                ].map(({ icon: Icon, href, label }) => (
                  <motion.a
                    key={label}
                    href={href}
                    whileHover={{ y: -3 }}
                    className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:border-[var(--gold)] transition-all duration-300 group"
                    aria-label={label}
                  >
                    <Icon className="w-5 h-5 text-[var(--text-muted)] group-hover:text-[var(--gold)] transition-colors" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[rgba(13,31,60,0.95)] rounded-2xl p-8 border border-[rgba(201,168,76,0.25)] backdrop-blur"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 rounded-lg bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 text-sm">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                  Full Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="w-full bg-white text-gray-900 placeholder:text-gray-500 border-gray-200"
                  disabled={isSubmitting || isSuccess}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="w-full bg-white text-gray-900 placeholder:text-gray-500 border-gray-200"
                  disabled={isSubmitting || isSuccess}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-white mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your needs..."
                  rows={6}
                  className="w-full resize-none bg-white text-gray-900 placeholder:text-gray-500 border-gray-200"
                  disabled={isSubmitting || isSuccess}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || isSuccess}
                className="w-full insolip-cta-primary py-6 text-lg"
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="inline-block"
                  >
                    <Send className="w-5 h-5" />
                  </motion.div>
                ) : isSuccess ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 className="w-5 h-5" />
                    <span>Message Sent!</span>
                  </motion.div>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
